// ─── Featmap 简易国际化系统 ───────────────────────────────────────────────────
// 用法：import { t, setLang, getLang } from '../i18n'
//      然后将页面文字替换为 t('key')
// 添加翻译：在 translations 对象中添加 key，en 和 zh 各填一条

export type Lang = 'en' | 'zh'

const STORAGE_KEY = 'featmap_lang'

export function getLang(): Lang {
  const stored = localStorage.getItem(STORAGE_KEY)
  return (stored === 'zh' || stored === 'en') ? stored : 'zh'
}

export function setLang(lang: Lang) {
  localStorage.setItem(STORAGE_KEY, lang)
  // 触发自定义事件，让订阅方强制刷新
  window.dispatchEvent(new Event('featmap_lang_change'))
}

export function toggleLang() {
  setLang(getLang() === 'zh' ? 'en' : 'zh')
}

// ─── 翻译词典 ─────────────────────────────────────────────────────────────────
const translations: Record<string, { en: string; zh: string }> = {
  // ── 通用 ──
  'save':               { en: 'Save',           zh: '保存' },
  'cancel':             { en: 'Cancel',          zh: '取消' },
  'delete':             { en: 'Delete',          zh: '删除' },
  'close':              { en: 'Close',           zh: '关闭' },
  'loading':            { en: 'Loading data...', zh: '加载中...' },
  'created':            { en: 'Created',         zh: '创建于' },
  'by':                 { en: 'by',              zh: '由' },
  'settings':           { en: 'Settings',        zh: '设置' },
  'name':               { en: 'Name',            zh: '名称' },
  'description':        { en: 'Description',     zh: '描述' },
  'status':             { en: 'Status',          zh: '状态' },
  'no_items':           { en: 'No items',        zh: '暂无数据' },

  // ── 登录/注册 ──
  'login_title':        { en: 'Log in to Featmap',          zh: '登录 Featmap' },
  'login_hint':         { en: 'Enter your email address and password.', zh: '请输入邮箱地址和密码。' },
  'login_btn':          { en: 'Log in',                     zh: '登录' },
  'login_no_account':   { en: 'Not a member?',              zh: '还没有账号？' },
  'login_create':       { en: 'Create an account',          zh: '立即注册' },
  'login_forgot':       { en: 'Forgotten your password?',   zh: '忘记密码？' },
  'login_reset':        { en: 'Reset password',             zh: '重置密码' },
  'login_error':        { en: 'Email or password is incorrect.', zh: '邮箱或密码不正确。' },
  'login_remember':     { en: 'Remember me',                    zh: '记住账号密码' },

  'signup_title':       { en: 'Create a Featmap account',   zh: '注册 Featmap 账号' },
  'signup_hint':        { en: 'Enter the name of your workspace, work email address and password.', zh: '请填写工作区名称、工作邮箱和密码。' },
  'signup_workspace':   { en: 'workspace name',             zh: '工作区名称（英文小写）' },
  'signup_name':        { en: 'Name, e.g. John Smith',      zh: '姓名，如：张三' },
  'signup_email':       { en: 'Work email',                 zh: '工作邮箱' },
  'signup_password':    { en: 'Password',                   zh: '密码' },
  'signup_btn':         { en: 'Create account',             zh: '创建账号' },
  'signup_has_account': { en: 'Already have an account?',   zh: '已有账号？' },
  'signup_login':       { en: 'Login',                      zh: '去登录' },

  // ── Header ──
  'header_projects':         { en: 'Projects',              zh: '项目列表' },
  'header_current_ws':       { en: 'Current workspace:',    zh: '当前工作区：' },
  'header_change':           { en: 'Change',                zh: '切换' },
  'header_ws_settings':      { en: 'Workspace settings',    zh: '工作区设置' },
  'header_my_workspaces':    { en: 'My workspaces',         zh: '我的工作区' },
  'header_logged_in_as':     { en: 'Logged in as',          zh: '当前用户：' },
  'header_account_settings': { en: 'Account settings',      zh: '账号设置' },
  'header_logout':           { en: 'Log out',               zh: '退出登录' },

  // ── 工作区 ──
  'workspaces_title':   { en: 'Workspaces',          zh: '工作区' },
  'workspace_new':      { en: 'New workspace',        zh: '新建工作区' },
  'workspace_count':    { en: 'workspace(s)',         zh: '个工作区' },
  'workspace_none':     { en: 'No workspaces',        zh: '暂无工作区' },
  'workspace_create_title': { en: 'Create a workspace',  zh: '创建工作区' },
  'workspace_name_label':   { en: 'Name',                zh: '名称' },
  'workspace_name_hint':    { en: 'Lowercase letters and numbers only.', zh: '仅限英文小写字母和数字。' },

  // ── 项目 ──
  'projects_title':     { en: 'Projects',          zh: '项目' },
  'project_new':        { en: 'New project',        zh: '新建项目' },
  'project_count':      { en: 'project(s)',         zh: '个项目' },
  'project_none':       { en: 'No projects',        zh: '暂无项目' },
  'project_create_title': { en: 'Create project',  zh: '创建项目' },

  // ── 看板/导出 ──
  'export_csv':         { en: 'Export CSV',         zh: '导出 CSV（无图片）' },
  'export_excel':       { en: 'Export Excel',       zh: '导出 Excel（无图片）' },
  'export_zip':         { en: 'Export ZIP (full)',   zh: '导出项目 ZIP（完整含图片）' },
  'import_zip':         { en: 'Import Project',      zh: '导入项目' },
  'import_zip_ing':     { en: 'Importing...',        zh: '导入中…' },
  'import_zip_ok':      { en: 'Import successful',   zh: '导入成功' },
  'import_zip_fail':    { en: 'Import failed',       zh: '导入失败，请检查文件格式' },
  'import_zip_name':    { en: 'Project name',        zh: '项目名称' },
  'import_zip_confirm': { en: 'Start Import',        zh: '开始导入' },
  'import_zip_cancel':  { en: 'Cancel',              zh: '取消' },
  'import_zip_title':   { en: 'Import project — set name', zh: '导入项目 — 设置项目名称' },
  'show_closed':        { en: 'Show closed',        zh: '显示已关闭' },
  'personas':           { en: 'Personas',           zh: '用户画像' },
  'share_link':         { en: 'Share link',         zh: '分享链接' },
  'view_only':          { en: 'VIEW ONLY',          zh: '仅查看' },

  // ── 故事卡 ──
  'feature_new':        { en: '+ Add feature',      zh: '+ 添加功能' },
  'milestone_new':      { en: 'Add release',        zh: '添加版本' },
  'workflow_new':       { en: 'Add goal',           zh: '添加目标' },
  'subworkflow_new':    { en: 'Add activity',       zh: '添加活动' },

  // ── 重置密码 ──
  'reset_title':        { en: 'Reset password',                   zh: '重置密码' },
  'reset_hint':         { en: 'Enter your email below. An email with instructions on how to reset your password will be sent.', zh: '请输入您的邮箱，我们将发送重置密码的邮件。' },
  'reset_btn':          { en: 'Reset password',                   zh: '发送重置邮件' },
  'reset_sent':         { en: 'An email has been sent with further instructions on how to reset your password.', zh: '重置密码邮件已发送，请查收邮箱。' },
  'reset_back':         { en: 'Back to',                          zh: '返回' },

  // ── 账号设置 ──
  'account_settings':       { en: 'Account settings',             zh: '账号设置' },
  'account_email_section':  { en: 'Email address',                zh: '邮箱地址' },
  'account_email_desc':     { en: 'Your email address is',        zh: '当前邮箱：' },
  'account_email_verified': { en: 'The email address is verified.', zh: '邮箱已验证。' },
  'account_email_not_verified': { en: 'The email address is not verified.', zh: '邮箱尚未验证。' },
  'account_email_pending':  { en: 'Email address verification missing', zh: '邮箱未完成验证' },
  'account_email_pending_desc': { en: 'A mail has been sent to', zh: '验证邮件已发送至' },
  'account_email_pending_desc2': { en: 'but we have not yet received verification.', zh: '，但尚未收到验证确认。' },
  'account_resend_email':   { en: 'Resend email',                 zh: '重新发送验证邮件' },
  'account_mail_sent':      { en: 'Mail sent.',                   zh: '邮件已发送。' },
  'account_change_email':   { en: 'Change email',                 zh: '修改邮箱' },
  'account_name_section':   { en: 'Name',                         zh: '姓名' },
  'account_name_desc':      { en: 'Your name is',                 zh: '当前姓名：' },
  'account_change_name':    { en: 'Change name',                  zh: '修改姓名' },
  'account_name_changed':   { en: 'Name is changed. Refresh your browser for it to take effect.', zh: '姓名已修改，请刷新页面生效。' },
  'account_password_section': { en: 'Password',                   zh: '密码' },
  'account_password_desc':  { en: 'To change your password, initiate a', zh: '如需修改密码，请发起' },
  'account_password_reset_link': { en: 'password reset',          zh: '密码重置' },
  'account_delete_section': { en: 'Delete account',               zh: '删除账号' },
  'account_delete_desc':    { en: 'By deleting your account, all your workspace memberships will be permanently deleted.', zh: '删除账号后，您在所有工作区的成员资格将被永久删除。' },
  'account_delete_btn':     { en: 'Delete account',               zh: '删除账号' },
  'account_something_wrong':{ en: 'Something went wrong.',        zh: '操作失败，请重试。' },
  'account_change_email_sent': { en: 'An email has been sent to your new email with instructions.', zh: '确认邮件已发送至新邮箱，请查收。' },
  'really_sure':            { en: 'Yes, I am really sure!',       zh: '确认，我要继续！' },

  // ── 工作区页面提示条 ──
  'ws_inactive_banner':     { en: 'The plan of this workspace is inactive. It is not possible to create or edit projects. If you are an owner you can manage workspace plans', zh: '该工作区的订阅计划已失效，无法创建或编辑项目。如果您是所有者，可在' },
  'ws_inactive_here':       { en: 'here',                         zh: '此处' },
  'ws_trial_banner':        { en: 'This workspace is running on a free trial. The trial will end', zh: '该工作区正在使用免费试用。试用将' },
  'ws_trial_today':         { en: 'today',                        zh: '今天' },
  'ws_trial_in':            { en: 'in',                           zh: '在' },
  'ws_trial_days':          { en: 'days',                         zh: '天后' },
  'ws_trial_cta':           { en: 'If you are an owner you can sign up for a paid plan', zh: '如果您是所有者，可以' },
  'ws_trial_here':          { en: 'here',                         zh: '订阅付费计划' },
  'ws_loading':             { en: 'Loading data...',              zh: '加载中...' },

  // ── 工作区设置 ──
  'wss_title':              { en: 'Workspace settings',           zh: '工作区设置' },
  'wss_my_membership':      { en: 'My membership',                zh: '我的成员身份' },
  'wss_joined':             { en: 'Joined',                       zh: '加入于' },
  'wss_leave':              { en: 'Leave workspace',              zh: '退出工作区' },
  'wss_plan':               { en: 'Plan',                         zh: '订阅计划' },
  'wss_plan_label':         { en: 'Plan',                         zh: '计划' },
  'wss_status':             { en: 'Status',                       zh: '状态' },
  'wss_num_members':        { en: 'Number of members',            zh: '成员数量' },
  'wss_start_time':         { en: 'Start time',                   zh: '开始时间' },
  'wss_expiry_time':        { en: 'Expiration time',              zh: '到期时间' },
  'wss_change_plan':        { en: 'Change plan',                  zh: '更改计划' },
  'wss_billing':            { en: 'Billing information',          zh: '账单信息' },
  'wss_eu_vat':             { en: 'EU VAT',                       zh: '欧盟增值税号' },
  'wss_billing_email':      { en: 'Billing e-mail address',       zh: '账单邮箱' },
  'wss_invites':            { en: 'Workspace invites',            zh: '工作区邀请' },
  'wss_pending_invites':    { en: 'pending invite(s)',            zh: '条待处理邀请' },
  'wss_invited_as':         { en: 'Invited as',                   zh: '邀请身份：' },
  'wss_by':                 { en: 'by',                           zh: '邀请人：' },
  'wss_send_invite':        { en: 'Send invitation',              zh: '发送邀请' },
  'wss_cancel_invite':      { en: 'Cancel invite',                zh: '取消邀请' },
  'wss_resend_invite':      { en: 'Resend invite',                zh: '重发邀请' },
  'wss_copy_invite_link':   { en: 'Copy invite link',             zh: '复制邀请链接' },
  'wss_copy_invite_ok':     { en: 'Copied!',                      zh: '已复制！' },

  'smtp_title':             { en: 'Email Service (SMTP)',          zh: '邮件服务配置（SMTP）' },
  'smtp_desc':              { en: 'Used for sending invite and notification emails.',  zh: '用于发送邀请邮件和通知邮件。' },
  'smtp_server':            { en: 'SMTP Server',                   zh: 'SMTP 服务器' },
  'smtp_port':              { en: 'Port',                          zh: '端口' },
  'smtp_user':              { en: 'Username',                      zh: '用户名（邮箱地址）' },
  'smtp_pass':              { en: 'Authorization Code / Password', zh: '授权码 / 密码' },
  'smtp_pass_hint':         { en: 'Leave blank to keep existing',  zh: '留空表示不修改现有授权码' },
  'smtp_from':              { en: 'From Address',                  zh: '发件人地址' },
  'smtp_save':              { en: 'Save',                          zh: '保存' },
  'smtp_saved':             { en: 'Saved!',                        zh: '已保存！' },
  'smtp_test':              { en: 'Send Test Email',               zh: '发送测试邮件' },
  'smtp_test_to':           { en: 'Send to (your email)',          zh: '发送到（你的邮箱）' },
  'smtp_test_ok':           { en: 'Test email sent! Please check your inbox.', zh: '测试邮件已发送，请查收邮箱！' },
  'smtp_test_fail':         { en: 'Failed to send test email.',    zh: '发送失败，请检查配置后重试。' },
  'smtp_preset':            { en: 'Quick Preset',                  zh: '快速预设' },
  'wss_members':            { en: 'Members',                      zh: '成员管理' },
  'wss_member_count':       { en: 'member(s)',                    zh: '位成员' },
  'wss_editor_count':       { en: 'editor(s)',                    zh: '位编辑者' },
  'wss_this_is_you':        { en: 'THIS IS YOU',                  zh: '（当前用户）' },
  'wss_change_role':        { en: 'Change role',                  zh: '修改角色' },
  'wss_delete_member':      { en: 'Delete',                       zh: '移除' },
  'wss_delete_ws':          { en: 'Delete workspace',             zh: '删除工作区' },
  'wss_delete_ws_desc':     { en: 'All projects in this workspace will be deleted permanently. You need to cancel any active plan before the workspace can be deleted.', zh: '该工作区下所有项目将被永久删除。删除前请先取消所有有效订阅。' },
  'wss_delete_ws_btn':      { en: 'Delete workspace',             zh: '删除工作区' },
  // subscription status labels
  'sub_incomplete':         { en: 'Inactive (please pay initial payment)',         zh: '未激活（请完成初始付款）' },
  'sub_incomplete_expired': { en: 'Inactive (initial payment not received)',        zh: '未激活（初始付款未收到）' },
  'sub_active':             { en: 'Active (subscribed to a paid monthly plan)',     zh: '有效（已订阅月付计划）' },
  'sub_trialing_inactive':  { en: 'Inactive (trial ended)',                        zh: '已失效（试用已结束）' },
  'sub_trialing_active':    { en: 'Active (trial)',                                zh: '有效（试用中）' },
  'sub_past_due':           { en: 'Inactive (payment is past due)',                zh: '已失效（付款逾期）' },
  'sub_canceled':           { en: 'Inactive (canceled by user or due to unpaid invoice)', zh: '已取消（用户取消或账单未付）' },

  // ── 语言切换 ──
  'lang_switch':        { en: '中文',               zh: 'English' },

  // ── 服务器设置 ──
  'server_settings_title':   { en: 'Server Settings',              zh: '服务器设置' },
  'server_settings_desc':    { en: 'Configure the port and public URL. Changes take effect after restarting the service.', zh: '配置服务端口和公网访问地址。修改后需重启服务才能生效。' },
  'server_port':             { en: 'Port',                         zh: '端口号' },
  'server_site_url':         { en: 'Site URL',                     zh: '访问地址（含端口）' },
  'server_site_url_hint':    { en: 'e.g. http://10.0.0.1:5010',   zh: '例：http://10.0.0.1:5010' },
  'server_save':             { en: 'Save',                         zh: '保存' },
  'server_save_ok':          { en: 'Saved. Please restart the service to apply changes.',  zh: '已保存。请在服务器上执行 ./start.sh 使配置生效。' },
  'server_save_fail':        { en: 'Save failed',                  zh: '保存失败' },
  'server_restart_notice':   { en: '⚠ Restart required: run ./start.sh on the server',   zh: '⚠ 需要重启：在服务器执行 ./start.sh' },
}

export function t(key: string): string {
  const lang = getLang()
  const entry = translations[key]
  if (!entry) return key  // fallback: 显示 key 本身，方便排查缺漏
  return entry[lang]
}
